import { esc, showModalErr, openModal } from '../ui/modal';
import { t } from '../ui/lang';
import { showAppsModal } from '../devices/device-modals';
import { showClickerCacheModal } from '../clicker/clicker-cache-modal';
import { isAdmin } from '../core/access';
// Command menu for a DUO (DuoPlus) device. Duo phones have no relay agent, so only the
// handful of actions that run straight over adb on the clicker-runner are offered:
// Enter PIN, Installed apps, a one-shot ADB shell, and — for admins — dropping the
// clicker's own caches for this phone. (Screen power, nav, wake, reboot
// and power-off were deliberately left out — they either need the scrcpy control channel
// duo has no stream for, or were not wanted.) Styling reuses the regular device-command
// popover classes so the two look identical.
let popoverEl = null;
let popoverEvents = null;
const icons = {
    pin: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="3"/><circle cx="8" cy="9" r="1"/><circle cx="12" cy="9" r="1"/><circle cx="16" cy="9" r="1"/><circle cx="8" cy="14" r="1"/><circle cx="12" cy="14" r="1"/><circle cx="16" cy="14" r="1"/></svg>',
    apps: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>',
    shell: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2" y="3" width="20" height="18" rx="2"/><path d="m7 9 3 3-3 3M13 15h4"/></svg>',
    caches: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13"/><path d="M10 11v6M14 11v6"/></svg>',
};
export function closeDuoCommandsPopover() {
    popoverEvents?.abort();
    popoverEvents = null;
    popoverEl?.remove();
    popoverEl = null;
}
export function showDuoCommandsPopover(anchor, udid, displayName) {
    closeDuoCommandsPopover();
    const popover = document.createElement('div');
    popover.className = 'device-command-popover';
    popover.innerHTML = `
        <div class="device-command-popover__header">
            <strong>${t('farmdev.device_commands')}</strong>
            <span>${esc(displayName)}</span>
        </div>
        <div id="duo-dc-error" class="modal__error" style="display:none"></div>
        <div class="device-command-list">
            <div class="device-pin-row">
                <input id="duo-dc-pin" type="password" inputmode="numeric" pattern="[0-9]*"
                    minlength="4" maxlength="16" autocomplete="off" autocapitalize="none" spellcheck="false"
                    data-lpignore="true" data-1p-ignore="true" data-bwignore="true" placeholder="${t('farmdev.pin_placeholder')}">
                <button type="button" class="device-command-btn device-command-btn--icon" id="duo-dc-enter-pin"
                    title="${t('farmdev.enter_pin')}" aria-label="${t('farmdev.enter_pin')}">${icons.pin}</button>
            </div>
            <button type="button" class="device-command-btn" id="duo-dc-apps">
                ${icons.apps}<span>${t('farmdev.installed_apps')}</span>
            </button>
            <button type="button" class="device-command-btn" id="duo-dc-shell">
                ${icons.shell}<span>${t('farmdev.adb_shell')}</span>
            </button>
            ${isAdmin() ? `<button type="button" class="device-command-btn" id="duo-dc-caches">
                ${icons.caches}<span>${t('clicker.caches_clear')}</span>
            </button>` : ''}
        </div>
    `;
    document.body.appendChild(popover);
    popoverEl = popover;
    popoverEvents = new AbortController();
    const eventOptions = { signal: popoverEvents.signal };
    // Anchor the popover to the button (same placement math as the regular popover).
    const anchorRect = anchor.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const margin = 8;
    const left = Math.min(window.innerWidth - popoverRect.width - margin, Math.max(margin, anchorRect.right - popoverRect.width));
    const belowTop = anchorRect.bottom + margin;
    const top = belowTop + popoverRect.height <= window.innerHeight - margin
        ? belowTop
        : Math.max(margin, anchorRect.top - popoverRect.height - margin);
    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;
    const errorEl = popover.querySelector('#duo-dc-error');
    // Enter PIN — types the code and confirms (ENTER) over adb on the runner. Prefills
    // from the last PIN sent to this device (shared key with the regular popover).
    const pinInput = popover.querySelector('#duo-dc-pin');
    const pinButton = popover.querySelector('#duo-dc-enter-pin');
    const pinStorageKey = `dc-pin:${udid}`;
    try {
        const savedPin = localStorage.getItem(pinStorageKey);
        if (savedPin)
            pinInput.value = savedPin;
    }
    catch { /* localStorage unavailable — ignore */ }
    const sendPin = async () => {
        const pin = pinInput.value.trim();
        if (!/^\d{4,16}$/.test(pin)) {
            showModalErr(errorEl, t('farmdev.pin_invalid'));
            pinInput.focus();
            return;
        }
        errorEl.style.display = 'none';
        pinButton.disabled = true;
        try {
            const res = await fetch(`/api/devices/${encodeURIComponent(udid)}/command`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ command: 'enter-pin', pin }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showModalErr(errorEl, data.error ?? t('farmdev.command_failed'));
                return;
            }
            pinInput.value = '';
            try {
                localStorage.setItem(pinStorageKey, pin);
            }
            catch { /* ignore */ }
        }
        catch {
            showModalErr(errorEl, t('farmdev.network_error'));
        }
        finally {
            pinButton.disabled = false;
        }
    };
    pinButton.addEventListener('click', () => { void sendPin(); });
    pinInput.addEventListener('keydown', (e) => { if (e.key === 'Enter')
        void sendPin(); });
    const appsButton = popover.querySelector('#duo-dc-apps');
    appsButton.addEventListener('click', () => {
        closeDuoCommandsPopover();
        void showAppsModal(udid, displayName);
    });
    const shellButton = popover.querySelector('#duo-dc-shell');
    shellButton.addEventListener('click', () => {
        closeDuoCommandsPopover();
        showDuoShellModal(udid, displayName);
    });
    const cachesButton = popover.querySelector('#duo-dc-caches');
    cachesButton?.addEventListener('click', () => {
        closeDuoCommandsPopover();
        showClickerCacheModal(udid, displayName);
    });
    const closeOnOutsideClick = (event) => {
        if (popover.contains(event.target) || anchor.contains(event.target))
            return;
        closeDuoCommandsPopover();
    };
    const closeOnEscape = (event) => {
        if (event.key === 'Escape')
            closeDuoCommandsPopover();
    };
    requestAnimationFrame(() => document.addEventListener('click', closeOnOutsideClick, { capture: true, ...eventOptions }));
    document.addEventListener('keydown', closeOnEscape, eventOptions);
}
// One-shot ADB shell for a duo device: type a command, POST it, append the output. Not
// the interactive WS shell relay devices use — a duo phone has no relay, so each command
// is a single `adb shell` round-trip via the runner.
function showDuoShellModal(udid, displayName) {
    const modal = openModal(`
        <h2 class="modal__title">${t('farmdev.adb_shell_title', { name: esc(displayName) })}</h2>
        <pre id="duo-shell-output" class="shell-output"></pre>
        <div class="shell-input-row">
            <span class="shell-prompt">$</span>
            <input id="duo-shell-input" class="shell-input" type="text"
                autocomplete="off" spellcheck="false" placeholder="${t('farmdev.command_placeholder')}" />
            <button type="button" id="duo-shell-send" class="modal__submit">${t('farmdev.send')}</button>
        </div>
    `);
    modal.classList.add('modal--wide');
    const outputEl = modal.querySelector('#duo-shell-output');
    const inputEl = modal.querySelector('#duo-shell-input');
    const sendBtn = modal.querySelector('#duo-shell-send');
    const append = (text) => {
        outputEl.textContent += text;
        outputEl.scrollTop = outputEl.scrollHeight;
    };
    const run = async () => {
        const cmd = inputEl.value.trim();
        if (!cmd)
            return;
        inputEl.value = '';
        append(`$ ${cmd}\n`);
        sendBtn.disabled = true;
        try {
            const res = await fetch(`/api/devices/${encodeURIComponent(udid)}/adb-shell`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cmd }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok)
                append(`[error] ${data.error ?? t('farmdev.command_failed')}\n`);
            else
                append((data.output ?? '') + (data.output && !data.output.endsWith('\n') ? '\n' : ''));
        }
        catch {
            append(`[error] ${t('farmdev.network_error')}\n`);
        }
        finally {
            sendBtn.disabled = false;
            inputEl.focus();
        }
    };
    sendBtn.addEventListener('click', () => { void run(); });
    inputEl.addEventListener('keydown', (e) => { if (e.key === 'Enter')
        void run(); });
    inputEl.focus();
}
